import { Helmet } from 'react-helmet-async';

interface SEOProps {
  language?: 'en' | 'de';
  title?: string;
  description?: string;
  noIndex?: boolean;
}

export const SEO = ({ language = 'en', title, description, noIndex = false }: SEOProps) => {
  const seo = {
    en: {
      title: "Stefan Kaiser - Executive IT Consulting | CIO CTO CEO Strategic Advisory",
      description: "Independent executive IT consulting for CIOs, CTOs, and CEOs. Strategic advisory, innovation scouting, design thinking workshops across Automotive, IT, Building, Telco, and Public Sector industries.",
      keywords: "executive IT consulting, CIO consulting, CTO advisory, CEO strategic partner, digital transformation, innovation scouting, design thinking workshops, IT strategy, technology leadership, automotive IT, telecommunications consulting, startup advisory",
      ogTitle: "Stefan Kaiser - Executive Consulting for C-Level Leaders",
      ogDescription: "Independent sparring partner for CIOs, CTOs & CEOs. Strategic advisory, innovation, and design thinking expertise across multiple industries.",
    },
    de: {
      title: "Stefan Kaiser - Executive IT Beratung | CIO CTO CEO Strategische Beratung",
      description: "Unabhängige Executive IT-Beratung für CIOs, CTOs und CEOs in Deutschland, Österreich und Schweiz. Strategische Beratung, Innovationsberatung, Design Thinking Workshops für Automotive, IT, Bau, Telekommunikation und öffentlichen Sektor.",
      keywords: "Executive IT Beratung Deutschland, CIO Beratung Schweiz, CTO Beratung Österreich, CEO strategischer Partner, Digitale Transformation DACH, Innovationsberatung, Design Thinking Workshops Deutschland, IT-Strategie Beratung, Technologie-Leadership, Automotive IT Beratung, Telekommunikation Consulting, Startup Beratung DACH, Interim CIO Deutschland",
      ogTitle: "Stefan Kaiser - Executive Beratung für C-Level Führungskräfte",
      ogDescription: "Unabhängiger Sparringspartner für CIOs, CTOs & CEOs in Deutschland, Österreich und Schweiz. Strategische Beratung, Innovation und Design Thinking Expertise.",
    }
  };

  const t = seo[language];
  const pageTitle = title || t.title;
  const pageDescription = description || t.description;

  return (
    <Helmet>
      <html lang={language} />
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      <meta name="keywords" content={t.keywords} />
      
      {/* Open Graph */}
      <meta property="og:title" content={t.ogTitle} />
      <meta property="og:description" content={t.ogDescription} />
      <meta property="og:locale" content={language === 'de' ? 'de_DE' : 'en_US'} />
      
      {/* Hreflang tags for international targeting */}
      <link rel="alternate" hrefLang="en" href="https://stefankaiser.net" />
      <link rel="alternate" hrefLang="de" href="https://stefankaiser.net" />
      <link rel="alternate" hrefLang="de-DE" href="https://stefankaiser.net" />
      <link rel="alternate" hrefLang="de-AT" href="https://stefankaiser.net" />
      <link rel="alternate" hrefLang="de-CH" href="https://stefankaiser.net" />
      <link rel="alternate" hrefLang="x-default" href="https://stefankaiser.net" />

      {/* Structured Data - Organization */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ProfessionalService",
          "name": "Stefan Kaiser Executive Consulting",
          "description": t.description,
          "url": "https://stefankaiser.net",
          "logo": "https://stefankaiser.net/favicon.png",
          "founder": {
            "@type": "Person",
            "name": "Stefan Kaiser"
          },
          "areaServed": [
            {
              "@type": "Country",
              "name": "Germany"
            },
            {
              "@type": "Country",
              "name": "Austria"
            },
            {
              "@type": "Country",
              "name": "Switzerland"
            }
          ],
          "serviceType": [
            "Executive IT Consulting",
            "CIO Advisory",
            "CTO Advisory",
            "Strategic Advisory",
            "Innovation Scouting",
            "Design Thinking Workshops"
          ],
          "priceRange": "€€€",
          "offers": {
            "@type": "Offer",
            "availability": "https://schema.org/InStock",
            "priceCurrency": "EUR"
          }
        })}
      </script>

      {/* Structured Data - Professional Service */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "serviceType": language === 'de' ? "Executive IT Beratung" : "Executive IT Consulting",
          "provider": {
            "@type": "Person",
            "name": "Stefan Kaiser"
          },
          "areaServed": ["DE", "AT", "CH"],
          "audience": {
            "@type": "Audience",
            "audienceType": "C-Level Executives, CIOs, CTOs, CEOs"
          }
        })}
      </script>

      {/* Structured Data - Breadcrumb */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": language === 'de' ? "Startseite" : "Home",
              "item": "https://stefankaiser.net"
            }
          ]
        })}
      </script>

      {/* Structured Data - FAQ for AI Search Engines */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": language === 'de' ? [
            {
              "@type": "Question",
              "name": "Was ist Executive IT Beratung?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Executive IT Beratung bietet strategische Unterstützung für CIOs, CTOs und CEOs bei IT-Transformationen, Innovationsstrategien und digitaler Transformation. Stefan Kaiser arbeitet als unabhängiger Sparringspartner für C-Level Führungskräfte in Deutschland, Österreich und der Schweiz."
              }
            },
            {
              "@type": "Question",
              "name": "Welche Dienstleistungen bietet Stefan Kaiser an?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Stefan Kaiser bietet Innovation & Trend Scouting, Design Thinking Workshops, strategische IT-Beratung und fungiert als unabhängiger Sparringspartner für C-Level Entscheidungen. Expertise in Automotive, IT, SAP, KI, Bau, Telekommunikation und öffentlichem Sektor."
              }
            },
            {
              "@type": "Question",
              "name": "Für wen ist die Executive Beratung geeignet?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Die Beratung richtet sich an CIOs, CTOs, CEOs und andere C-Level Führungskräfte in Deutschland, Österreich und der Schweiz, die strategische IT-Entscheidungen treffen, Innovationen vorantreiben oder komplexe digitale Transformationen durchführen möchten."
              }
            },
            {
              "@type": "Question",
              "name": "Was sind Design Thinking Workshops?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Design Thinking Workshops sind strukturierte Innovationssitzungen, die bahnbrechende Lösungen durch kollaborative Ideenfindung ermöglichen. Stefan Kaiser moderiert diese Workshops mit nachweisbaren Erfolgsbilanzen in verschiedenen Branchen."
              }
            },
            {
              "@type": "Question",
              "name": "In welchen Branchen hat Stefan Kaiser Expertise?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Stefan Kaiser verfügt über umfassende Expertise in Automotive, Informationstechnologie, SAP, Fertigung, Software, KI-Entwicklung, Geschäftsmodellen, Bau & Konstruktion, Telekommunikation, öffentlichem Sektor sowie Startups & Scale-ups."
              }
            }
          ] : [
            {
              "@type": "Question",
              "name": "What is Executive IT Consulting?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Executive IT Consulting provides strategic support for CIOs, CTOs, and CEOs in IT transformations, innovation strategies, and digital transformation. Stefan Kaiser works as an independent sparring partner for C-level executives across Germany, Austria, and Switzerland."
              }
            },
            {
              "@type": "Question",
              "name": "What services does Stefan Kaiser offer?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Stefan Kaiser offers Innovation & Trend Scouting, Design Thinking Workshops, Strategic Advisory, and serves as an Independent Sparring Partner for C-level decision-making. Expertise spans Automotive, IT, SAP, AI, Building, Telecommunications, Public Sector, and Startups."
              }
            },
            {
              "@type": "Question",
              "name": "Who is Executive Consulting for?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Executive consulting is designed for CIOs, CTOs, CEOs, and C-level executives in Germany, Austria, and Switzerland who need to make strategic IT decisions, drive innovation, or navigate complex digital transformations."
              }
            },
            {
              "@type": "Question",
              "name": "What are Design Thinking Workshops?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Design Thinking Workshops are structured innovation sessions that facilitate breakthrough solutions through collaborative ideation. Stefan Kaiser moderates these workshops with proven track records of delivering actionable value across multiple industries."
              }
            },
            {
              "@type": "Question",
              "name": "What industries does Stefan Kaiser have expertise in?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Stefan Kaiser has comprehensive expertise in Automotive, Information Technology, SAP, Manufacturing, Software, AI Development, Business Models, Building & Construction, Telecommunications, Public Sector, and Startups & Scale-ups."
              }
            }
          ]
        })}
      </script>

      {/* Structured Data - Person (for AI assistant recognition) */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Person",
          "name": "Stefan Kaiser",
          "jobTitle": language === 'de' ? "Executive IT Berater" : "Executive IT Consultant",
          "description": language === 'de' 
            ? "Unabhängiger Executive IT Berater und strategischer Sparringspartner für CIOs, CTOs und CEOs in Deutschland, Österreich und der Schweiz. Spezialisiert auf Innovation Scouting, Design Thinking und digitale Transformation."
            : "Independent Executive IT Consultant and strategic sparring partner for CIOs, CTOs, and CEOs across Germany, Austria, and Switzerland. Specialized in Innovation Scouting, Design Thinking, and digital transformation.",
          "knowsAbout": [
            "Executive IT Consulting",
            "CIO Advisory",
            "CTO Advisory",
            "Strategic IT Planning",
            "Innovation Scouting",
            "Design Thinking",
            "Digital Transformation",
            "Technology Leadership",
            "Automotive IT",
            "SAP Consulting",
            "AI Strategy",
            "Telecommunications",
            "Startup Advisory"
          ],
          "alumniOf": language === 'de' ? "Hochschule Mannheim" : "Mannheim University of Applied Sciences",
          "workLocation": {
            "@type": "Place",
            "address": {
              "@type": "PostalAddress",
              "addressCountry": ["DE", "AT", "CH"]
            }
          }
        })}
      </script>
    </Helmet>
  );
};
